/* =====================================================================
 * @auw/i18n — singleton i18next bootstrap.
 *
 * Split out of `./index.ts` so child modules (agentProfile,
 * profileLocale, profileJobInterests, formatters, useAppLocale) can
 * `import i18n from './bootstrap'` without pulling in the full public
 * re-export surface. This keeps the init graph acyclic:
 *
 *   bootstrap.ts         ← nobody imports anyone in this package
 *     ↑
 *   agentProfile.ts / profileLocale.ts / profileJobInterests.ts /
 *   formatters.ts / useAppLocale.ts
 *     ↑
 *   index.ts             ← re-exports everything above
 *
 * The module-eval order relies on the host having published the
 * active tenant manifest via `setActiveTenantManifest(...)` BEFORE
 * any `@auw/i18n` subtree is pulled in. B2C / enterprise hosts
 * guarantee this via the `import './tenant/storage'` /
 * `import './tenants/bootstrap'` side-effect on line 1 of main.tsx.
 * ===================================================================== */

import i18next, {
  type PostProcessorModule,
  type TOptions,
  type i18n as I18nInstance,
} from 'i18next';
import { initReactI18next } from 'react-i18next';

import { getActiveTenantManifest } from '@auw/tenant';

import { NAMESPACES, RESOURCES } from './resources';
import { resolveAppLocale, type AppLocale } from './resolveAppLocale';
import './types';

/* ---------------------------------------------------------------------
 * productName resolution — read lazily on first translation that
 * substitutes a brand placeholder.
 *
 * Why lazy (and not module-eval as it used to be):
 *   The host app publishes the active manifest from
 *   `frontend/askyourai/src/tenant/storage.ts` /
 *   `frontend/apps/enterprise/src/tenants/bootstrap.ts` via a
 *   side-effect import that fires before any `@auw/i18n` consumer
 *   pulls in this module. ESM declaration order normally guarantees
 *   that ordering, BUT Vite/Rollup's chunk-split heuristics will
 *   happily hoist the host's tiny side-effect bootstrap module into
 *   a lazy chunk whenever another chunk (e.g. `feature-preview-pdf`,
 *   which transitively imports `@auw/tenant`) becomes a co-tenant.
 *   When that happens, `setActiveTenantManifest()` only runs once
 *   the user opens a PDF preview — the host bundle's i18n module
 *   body has already evaluated and crashed with
 *   "[auw/tenant] Active tenant manifest has not been set".
 *
 *   We saw this regression on 2026-04-29 (dev.askyour.ai blank
 *   screen + activeTenant.ts:58 throw). Pulling the manifest read
 *   into lazy `getProductName()` decouples i18n bootstrap from any
 *   build-time chunking decision: the read happens during the very
 *   first `t(...)` call from React render time, which is always
 *   after the host has wired up its providers.
 *
 * Switching tenants still requires a full reload (same as the
 * existing B2C assumption), so memoising the value on first read is
 * safe — there is no "tenant changed mid-session" path to invalidate.
 * ------------------------------------------------------------------- */

let _productNameCache: string | null = null;
function getProductName(): string {
  if (_productNameCache !== null) return _productNameCache;
  _productNameCache = getActiveTenantManifest().identity.appName;
  return _productNameCache;
}

/* ---------------------------------------------------------------------
 * Brand placeholder postProcessor.
 *
 * Runs against every translation result (see init option
 * `postProcess: ['brandPlaceholders']` below). Uses a fast-path check
 * to avoid the regex when the string has no placeholder at all.
 * ------------------------------------------------------------------- */

const PRODUCT_NAME_PLACEHOLDER = /\{\{\s*productName\s*\}\}/g;

const brandPlaceholdersPostProcessor: PostProcessorModule = {
  type: 'postProcessor',
  name: 'brandPlaceholders',
  process(value: unknown, _key: string | string[], _options: TOptions, _translator: unknown): string {
    if (typeof value !== 'string') return value as string;
    if (!value.includes('{{')) return value;
    return value.replace(PRODUCT_NAME_PLACEHOLDER, getProductName());
  },
};

/* ---------------------------------------------------------------------
 * Initial locale: browser language → AppLocale → 'en' fallback.
 * SSR / pre-render safe (guards `window`, `navigator`, `document`).
 * ------------------------------------------------------------------- */

const initialLocale: AppLocale =
  typeof window !== 'undefined' && typeof navigator !== 'undefined'
    ? resolveAppLocale(navigator.language)
    : 'en';

if (typeof document !== 'undefined') {
  document.documentElement.lang = initialLocale;
}

/* ---------------------------------------------------------------------
 * i18next bootstrap. `init()` resolves synchronously because we pass
 * all resources inline and omit any backend plugin — callers can use
 * `i18n.t(...)` immediately after this module finishes evaluating.
 * ------------------------------------------------------------------- */

void i18next
  .use(initReactI18next)
  .use(brandPlaceholdersPostProcessor)
  .init({
    lng: initialLocale,
    resources: RESOURCES,
    supportedLngs: ['en', 'ko', 'ja', 'zh-CN', 'zh-TW'],
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: NAMESPACES as unknown as string[],
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    postProcess: ['brandPlaceholders'],
  });

const i18n: I18nInstance = i18next;
export default i18n;
